/**
 * DemoRequestModal — extracted from /pages/Pricing.jsx (was 570 lines).
 *
 * Self-contained dialog used by the "Talk to sales" CTA. Owns its own
 * form state + submit handler. Parent passes the contextual `persona`
 * and `modules` array so the demo request lands with the prospect's
 * pre-selected interest.
 */
import React, { useState } from 'react';
import { ArrowRight, X, MessageCircle } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';

export default function DemoRequestModal({ onClose, persona, modules }) {
  const [form, setForm] = useState({ name: '', email: '', company: '', message: '' });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error('Name and email are required.');
      return;
    }
    setBusy(true);
    try {
      await api.post('/billing/demo-request', {
        ...form,
        persona,
        modules,
        source: 'pricing_page',
      });
      toast.success('Thank you. Our team will reach out within one business day.');
      onClose();
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || 'Could not submit. Please email us directly.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      data-testid="demo-modal"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <form onSubmit={submit} className="bg-white dark:bg-[#0F0F0F] rounded-xl border border-[rgba(174,82,234,0.30)] p-6 w-full max-w-md shadow-2xl">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <MessageCircle size={16} className="text-[var(--ve-violet-bright)]" />
            <span className="text-[10px] uppercase tracking-[0.22em] font-bold text-[var(--ve-violet-bright)]">Request a demo</span>
          </div>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-700 dark:hover:text-white" data-testid="demo-modal-close">
            <X size={16} />
          </button>
        </div>
        <h3 className="display text-2xl">Let&apos;s talk.</h3>
        <p className="text-xs text-slate-600 dark:text-slate-300 mt-2">
          Our team will reach out within one business day to walk you through Vigilant Entities and tailor the modules to your organisation.
        </p>
        <div className="mt-5 space-y-3">
          <input required placeholder="Full name *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="demo-name"
            className="w-full rounded-md border border-slate-200 dark:border-white/15 bg-white dark:bg-white/[0.03] px-3 py-2.5 text-sm focus:outline-none focus:border-[var(--ve-violet)]" />
          <input required type="email" placeholder="Work email *" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="demo-email"
            className="w-full rounded-md border border-slate-200 dark:border-white/15 bg-white dark:bg-white/[0.03] px-3 py-2.5 text-sm focus:outline-none focus:border-[var(--ve-violet)]" />
          <input placeholder="Company / Organisation" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} data-testid="demo-company"
            className="w-full rounded-md border border-slate-200 dark:border-white/15 bg-white dark:bg-white/[0.03] px-3 py-2.5 text-sm focus:outline-none focus:border-[var(--ve-violet)]" />
          <textarea placeholder="Tell us about your use-case (optional)" rows={3} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="demo-message"
            className="w-full rounded-md border border-slate-200 dark:border-white/15 bg-white dark:bg-white/[0.03] px-3 py-2.5 text-sm focus:outline-none focus:border-[var(--ve-violet)] resize-none" />
        </div>
        {(persona || modules.length > 0) && (
          <div className="mt-4 px-3 py-2 rounded border border-[rgba(174,82,234,0.20)] bg-[rgba(174,82,234,0.05)] text-[11px] text-slate-600 dark:text-slate-300">
            <span className="uppercase tracking-[0.15em] font-bold text-[var(--ve-violet-bright)] mr-1.5">Context:</span>
            {persona && <span className="capitalize">{persona}</span>}
            {modules.length > 0 && <span> · {modules.length} modules pre-selected</span>}
          </div>
        )}
        <button type="submit" disabled={busy} data-testid="demo-submit" className="mt-5 w-full ve-btn-primary justify-center disabled:opacity-60">
          {busy ? 'Sending…' : 'Request demo'} <ArrowRight size={14} />
        </button>
      </form>
    </div>
  );
}
